import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Collapse,
  TextField,
  Chip,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { getItems } from '../../services/ApiService';
import { Item, ItemStatus, Responsible } from '../../models/SafetyPortalTypes';
import Spinner from '../../components/Spinner/Spinner';
import { format } from 'date-fns';
import styles from './ItemsList.module.css';

type TabValue = 'internal' | 'contractor';

type DateFieldKey = 'lastCheckDate' | 'nextCheckDate' | 'createdAt' | 'updatedAt';

type FiltersState = {
  status?: ItemStatus;
  search?: string;
  needsCheckWithinDays?: number;
  isActive?: boolean;
  responsible?: Responsible;
  // Date range filters
  lastCheckDateFrom?: string;
  lastCheckDateTo?: string;
  nextCheckDateFrom?: string;
  nextCheckDateTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  updatedAtFrom?: string;
  updatedAtTo?: string;
};

const ItemsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('internal');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FiltersState>({ isActive: true, responsible: 'internal' });
  const [draftFilters, setDraftFilters] = useState<FiltersState>({ isActive: true, responsible: 'internal' });
  // Date filter builder state
  const [selectedDateField, setSelectedDateField] = useState<DateFieldKey | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Mapping for date field keys to i18n labels and filter param names
  const dateFieldOptions: { key: DateFieldKey; labelKey: string; fromParam: keyof FiltersState; toParam: keyof FiltersState }[] = [
    { key: 'lastCheckDate', labelKey: 'Items.Filters.LastCheck', fromParam: 'lastCheckDateFrom', toParam: 'lastCheckDateTo' },
    { key: 'nextCheckDate', labelKey: 'Items.Filters.NextCheck', fromParam: 'nextCheckDateFrom', toParam: 'nextCheckDateTo' },
    { key: 'createdAt', labelKey: 'Items.Filters.Created', fromParam: 'createdAtFrom', toParam: 'createdAtTo' },
    { key: 'updatedAt', labelKey: 'Items.Filters.Updated', fromParam: 'updatedAtFrom', toParam: 'updatedAtTo' },
  ];

  // Get internal items for count
  const { data: internalItems = [] } = useQuery<Item[]>({
    queryKey: ['items', { isActive: true, responsible: 'internal' }],
    queryFn: () => getItems({ isActive: true, responsible: 'internal' }),
  });

  // Get contractor items for count
  const { data: contractorItems = [] } = useQuery<Item[]>({
    queryKey: ['items', { isActive: true, responsible: 'contractor' }],
    queryFn: () => getItems({ isActive: true, responsible: 'contractor' }),
  });

  // Get filtered items based on current tab and filters
  const { data: items = [], isLoading, isFetching, isError, refetch } = useQuery<Item[]>({
    queryKey: ['items', filters],
    queryFn: () => getItems(filters),
  });

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
    const nextFilters: FiltersState = { 
      isActive: true,
      responsible: newValue 
    };
    setFilters(nextFilters);
    setDraftFilters(nextFilters);
  };

  // Check if item needs check within 14 days
  const needsCheckSoon = (item: Item): boolean => {
    if (!item.nextCheckDate || !item.isActive) return false;
    const nextCheck = new Date(item.nextCheckDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    const daysDiff = Math.ceil((nextCheck.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff >= 0 && daysDiff <= 14;
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setDraftFilters((prev) => ({
      ...prev,
      [name]: value || undefined,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDraftFilters((prev) => ({
      ...prev,
      search: value.trim() || undefined,
    }));
  };

  const handleAddDateFilter = () => {
    if (!selectedDateField || (!dateFrom && !dateTo)) return;
    const option = dateFieldOptions.find((o) => o.key === selectedDateField);
    if (!option) return;
    setDraftFilters((prev) => ({
      ...prev,
      [option.fromParam]: dateFrom || undefined,
      [option.toParam]: dateTo || undefined,
    }));
    setSelectedDateField('');
    setDateFrom('');
    setDateTo('');
  };

  const handleRemoveDateFilter = (fieldKey: DateFieldKey) => {
    const option = dateFieldOptions.find((o) => o.key === fieldKey);
    if (!option) return;
    setDraftFilters((prev) => ({
      ...prev,
      [option.fromParam]: undefined,
      [option.toParam]: undefined,
    }));
  };

  // Get active date filters from draft for displaying chips
  const getActiveDateFilters = (): { key: DateFieldKey; label: string; from?: string; to?: string }[] => {
    return dateFieldOptions
      .filter((opt) => draftFilters[opt.fromParam] || draftFilters[opt.toParam])
      .map((opt) => ({
        key: opt.key,
        label: t(opt.labelKey),
        from: draftFilters[opt.fromParam] as string | undefined,
        to: draftFilters[opt.toParam] as string | undefined,
      }));
  };

  const handleActiveFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setDraftFilters((prev) => ({
      ...prev,
      isActive: value === '' ? undefined : value === 'true',
    }));
  };

  const applyFilters = () => {
    // Ensure responsible filter is maintained from active tab
    setFilters({ ...draftFilters, responsible: activeTab });
  };

  // Initialize filters when component mounts or tab changes
  useEffect(() => {
    const nextFilters: FiltersState = { 
      isActive: true,
      responsible: activeTab 
    };
    setFilters(nextFilters);
    setDraftFilters(nextFilters);
  }, [activeTab]);

  const handleFilterToggle = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const getStatusColor = (status: ItemStatus) => {
    switch (status) {
      case 'checked':
        return '#4caf50';
      case 'overdue':
        return '#f44336';
      case 'pending':
        return '#ff9800';
      default:
        return '#757575';
    }
  };


  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className={styles.container}>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>{t('Items.Title')}</h2>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterToggle}
            sx={{
              backgroundColor: '#ffffff',
              borderColor: '#78B26E',
              color: '#333333',
              '&:hover': {
                backgroundColor: '#E1ECE2',
                borderColor: '#5E8856',
              },
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                marginLeft: 0,
              },
              '[dir="rtl"] & .MuiButton-startIcon': {
                marginLeft: '8px',
                marginRight: 0,
              },
            }}
          >
            {isFilterOpen ? t('Filters.Close') : t('Filters.Open')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/items/create')}
            sx={{
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                marginLeft: 0,
              },
              '[dir="rtl"] & .MuiButton-startIcon': {
                marginLeft: '8px',
                marginRight: 0,
              },
            }}
          >
            {t('Items.Create')}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="items tabs">
          <Tab 
            label={
              <Badge 
                badgeContent={internalItems.length} 
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    right: '-16px',
                    top: '-4px',
                    transform: 'none',
                    '[dir="rtl"] &': {
                      right: 'auto',
                      left: '-16px',
                    },
                  },
                }}
              >
                <Box component="span" sx={{ display: 'inline-block', paddingRight: '12px', '[dir="rtl"] &': { paddingRight: 0, paddingLeft: '12px' } }}>
                  {t('Items.Tabs.Internal') || 'פנימי'}
                </Box>
              </Badge>
            }
            value="internal"
            sx={{
              '&.Mui-selected': {
                color: '#78B26E',
              },
            }}
          />
          <Tab 
            label={
              <Badge 
                badgeContent={contractorItems.length} 
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    right: '-16px',
                    top: '-4px',
                    transform: 'none',
                    '[dir="rtl"] &': {
                      right: 'auto',
                      left: '-16px',
                    },
                  },
                }}
              >
                <Box component="span" sx={{ display: 'inline-block', paddingRight: '12px', '[dir="rtl"] &': { paddingRight: 0, paddingLeft: '12px' } }}>
                  {t('Items.Tabs.Contractors') || 'קבלנים'}
                </Box>
              </Badge>
            }
            value="contractor"
            sx={{
              '&.Mui-selected': {
                color: '#78B26E',
              },
            }}
          />
        </Tabs>
      </Box>

      <Collapse in={isFilterOpen}>
        <Box mb={2} p={2} sx={{ border: '1px solid #ccc', borderRadius: 1 }}>
          <TextField
            fullWidth
            margin="normal"
            label={t('Items.Filters.Search') || 'Search'}
            value={draftFilters.search || ''}
            onChange={handleSearchChange}
            placeholder={t('Items.Filters.SearchPlaceholder') || 'Search in name, description, location, notes, model, license number...'}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>{t('Items.Filters.Status')}</InputLabel>
            <Select
              name="status"
              value={draftFilters.status || ''}
              onChange={handleFilterChange}
              label={t('Items.Filters.Status')}
            >
              <MenuItem value="">{t('General.Labels.All')}</MenuItem>
              <MenuItem value="pending">{t('Items.Status.Pending')}</MenuItem>
              <MenuItem value="checked">{t('Items.Status.Checked')}</MenuItem>
              <MenuItem value="overdue">{t('Items.Status.Overdue')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>{t('Items.Filters.ActiveStatus') || 'Active Status'}</InputLabel>
            <Select
              name="isActive"
              value={draftFilters.isActive === undefined ? '' : draftFilters.isActive.toString()}
              onChange={handleActiveFilterChange}
              label={t('Items.Filters.ActiveStatus') || 'Active Status'}
            >
              <MenuItem value="">{t('General.Labels.All')}</MenuItem>
              <MenuItem value="true">{t('Items.Filters.Active') || 'Active'}</MenuItem>
              <MenuItem value="false">{t('Items.Filters.Inactive') || 'Inactive'}</MenuItem>
            </Select>
          </FormControl>

          {/* Date Range Filter Builder */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mt: 1 }}>
            <FormControl sx={{ minWidth: '160px', flex: 1 }} margin="dense">
              <InputLabel size="small">{t('Items.Filters.DateField') || 'Date Field'}</InputLabel>
              <Select
                size="small"
                value={selectedDateField}
                onChange={(e) => setSelectedDateField(e.target.value as DateFieldKey | '')}
                label={t('Items.Filters.DateField') || 'Date Field'}
              >
                <MenuItem value="">&nbsp;</MenuItem>
                {dateFieldOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>{t(opt.labelKey)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label={t('Items.Filters.From') || 'From'}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!selectedDateField}
              sx={{ flex: 1, minWidth: '140px' }}
              margin="dense"
            />
            <TextField
              size="small"
              type="date"
              label={t('Items.Filters.To') || 'To'}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!selectedDateField}
              sx={{ flex: 1, minWidth: '140px' }}
              margin="dense"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddDateFilter}
              disabled={!selectedDateField || (!dateFrom && !dateTo)}
              sx={{
                minWidth: 'auto',
                px: 2,
                height: '40px',
                borderColor: '#78B26E',
                color: '#333',
                '&:hover': { backgroundColor: '#E1ECE2', borderColor: '#5E8856' },
              }}
            >
              {t('Items.Filters.AddDateFilter') || 'Add'}
            </Button>
          </Box>

          {/* Active date filter chips */}
          {getActiveDateFilters().length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {getActiveDateFilters().map((df) => (
                <Chip
                  key={df.key}
                  label={`${df.label}: ${df.from || '...'} — ${df.to || '...'}`}
                  onDelete={() => handleRemoveDateFilter(df.key)}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  size="small"
                  sx={{ backgroundColor: '#E1ECE2', '& .MuiChip-deleteIcon': { color: '#5E8856' } }}
                />
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            <Button
              variant="contained"
              onClick={applyFilters}
              sx={{
                backgroundColor: '#78B26E',
                '&:hover': { backgroundColor: '#5E8856' },
              }}
            >
              {t('Filters.Apply') || 'Apply Filters'}
            </Button>
          </Box>
        </Box>
      </Collapse>

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('Items.Messages.LoadError')}
        </Alert>
      ) : isLoading || isFetching ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Spinner />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Items.Table.ID')}</TableCell>
                <TableCell>{t('Items.Table.Name')}</TableCell>
                <TableCell>{t('Items.Table.Model')}</TableCell>
                <TableCell>{t('Items.Table.LicenseNumber')}</TableCell>
                <TableCell>{t('Items.Table.Location')}</TableCell>
                <TableCell>{t('Items.Table.Status')}</TableCell>
                <TableCell>{t('Items.Table.Active') || 'Active'}</TableCell>
                <TableCell>{t('Items.Table.LastCheck')}</TableCell>
                <TableCell>{t('Items.Table.NextCheck')}</TableCell>
                <TableCell>{t('Items.Table.Checks')}</TableCell>
                <TableCell>{t('Items.Table.Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {t('Items.Messages.NoItems')}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const needsCheck = needsCheckSoon(item);
                  const hasMissingFields = !item.responsible || !item.checkIntervalMonths;
                  return (
                    <TableRow 
                      key={item._id} 
                      sx={{ 
                        opacity: item.isActive === false ? 0.6 : 1,
                        backgroundColor: hasMissingFields ? '#fce4ec' : needsCheck ? '#fff3cd' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasMissingFields && (
                            <Tooltip title={t('Items.Messages.MissingFieldsWarning')}>
                              <WarningIcon sx={{ color: '#f44336', fontSize: '20px' }} />
                            </Tooltip>
                          )}
                          {needsCheck && !hasMissingFields && (
                            <Tooltip title={t('Items.NeedsCheckSoon') || 'Needs check within 14 days'}>
                              <WarningIcon sx={{ color: '#ff9800', fontSize: '20px' }} />
                            </Tooltip>
                          )}
                          {item.itemId}
                        </Box>
                      </TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.model || '-'}</TableCell>
                      <TableCell>{item.licenseNumber || '-'}</TableCell>
                      <TableCell>{item.location}</TableCell>
                      <TableCell>
                        <span style={{ color: getStatusColor(item.status) }}>
                          {t(`Items.Status.${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.isActive ? (t('Items.Filters.Active') || 'Active') : (t('Items.Filters.Inactive') || 'Inactive')}
                          size="small"
                          color={item.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {item.lastCheckDate ? format(new Date(item.lastCheckDate), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {item.nextCheckDate ? format(new Date(item.nextCheckDate), 'dd/MM/yyyy') : '-'}
                          {needsCheck && (
                            <Chip
                              label={t('Items.Soon') || 'Soon'}
                              size="small"
                              color="warning"
                              sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{item.checks?.length || 0}</TableCell>
                      <TableCell>
                        <Tooltip title={t('Items.Details.View')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/items/${item._id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('General.Labels.Edit')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/items/edit/${item._id}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ItemsList;

